import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom'; // Import useLocation
import './Header.css'; // Shared styles for the header
import llStorage from '../llStorage'; // Assuming you have a local storage utility
import axiosInstance from '../axiosInstance';
import { t } from '../i18n'; // Import the translation utility
import { sortLanguagesAlpha } from '../utils/locationDefaults';
import { hasFeatureAccess } from '../utils/planGating';

// Add this helper at the top, after your imports
const languageFlags = {
  cs: '🇨🇿',
  en: '🇬🇧',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
};

const languages = [
  { code: 'cs', label: 'Czech' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
];

function normalizeStoredImage(value) {
  if (!value || value === 'undefined' || value === 'null') {
    return null;
  }

  return value;
}

function detectBrowserLanguage() {
  const navLang = navigator.language || navigator.userLanguage || 'en';
  const shortLang = navLang.split('-')[0].toLowerCase();
  const supported = languages.map(l => l.code);
  return supported.includes(shortLang) ? shortLang : 'en';
}

function Header({ isLoggedIn, setIsLoggedIn, language, setLanguage, testMode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHorizontalMenu, setIsHorizontalMenu] = useState(false); // State to track menu layout
  const sortedLanguageOptions = useMemo(() => sortLanguagesAlpha(languages, language || 'en'), [language]);
  const [newMessageCount, setNewMessageCount] = useState(0); // State for new message count
  const [theme, setTheme] = useState(llStorage.getItem('theme') || 'dark');
  const navigate = useNavigate();
  const location = useLocation(); // Get the current route
  const [userImage, setUserImage] = useState(null);
  const [userInitialsShort4Chars, setUserInitialsShort4Chars] = useState('');
  const [canManageLlpnCustomerCards, setCanManageLlpnCustomerCards] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentSlogan, setCurrentSlogan] = useState(0);
  const menuContainerRef = useRef(null);

  const syncHeaderProfileFromStorage = () => {
    const imageUrl = normalizeStoredImage(llStorage.getItem('userImage'));
    setUserImage(imageUrl);
    setUserInitialsShort4Chars((llStorage.getItem('userInitialsShort4Chars') || '').trim().slice(0, 4));
    setCanManageLlpnCustomerCards(llStorage.getItem('userLLPNClientExtCutNo') === 'true');
  };

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    llStorage.setItem('language', lang);
    // Optionally: trigger i18n language change here if using i18n
  };

  // Replace slogans array with translated slogans
  const slogans = t('header.mainSlogans', language);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlogan((prev) => (prev + 1) % slogans.length);
    }, 5000); // Rotate every 5 seconds
    return () => clearInterval(interval);
  }, [slogans.length]);
  
  useEffect(() => {
    const token = llStorage.getItem('token');
    setIsLoggedIn(!!token); // Update the isLoggedIn state based on the token

    // Sync profile badge from local storage first (fast path)
    syncHeaderProfileFromStorage();

    // Then refresh from API (authoritative source)
    if (token) {
      axiosInstance
        .get('/users/me')
        .then((response) => {
          setIsAdmin(response?.data?.userRole === 'admin');
          const ownerImage = normalizeStoredImage(response?.data?.ownerImage);
          const initials = (response?.data?.initialsShort4Chars || '').trim().slice(0, 4);

          if (ownerImage) {
            llStorage.setItem('userImage', ownerImage);
          } else {
            llStorage.removeItem('userImage');
          }

          llStorage.setItem('userInitialsShort4Chars', initials);
          llStorage.setItem('userLLPNClientExtCutNo', response?.data?.LLPNClientExtCutNo ? 'true' : 'false');
          llStorage.setItem('listPageSize', response?.data?.listPageSize || '20');
          setUserImage(ownerImage);
          setUserInitialsShort4Chars(initials);
          setCanManageLlpnCustomerCards(!!response?.data?.LLPNClientExtCutNo);
        })
        .catch(() => {
          setIsAdmin(false);
        });
    } else {
      setIsAdmin(false);
    }

    // Fetch new message count if logged in
    //console.log('Before fetchNewMessageCount. Token:', token);
    if (token) {
      const userId = llStorage.getItem('userId');
      fetchNewMessageCount(userId);
    }
  }, [location, setIsLoggedIn]); // Trigger this effect on route changes

  useEffect(() => {
    const onProfileChanged = () => syncHeaderProfileFromStorage();
    window.addEventListener('ll:userProfileChanged', onProfileChanged);
    return () => window.removeEventListener('ll:userProfileChanged', onProfileChanged);
  }, []);

  // Fetch new message count from the API
  const fetchNewMessageCount = async (userId) => {
    try {
      const response = await axiosInstance.get(`/messages/${userId}/newMsgCount`);
      setNewMessageCount(response.data.newMessageCount || 0);
      //console.log('New message count:', response.data.newMessageCount);
    } catch (error) {
      console.error('Error fetching new message count:', error);
    }
  };

  // Check if there is enough space for a horizontal menu
  useEffect(() => {
    const handleResize = () => {
      //logged inn users have allways menu button
      if (isLoggedIn) {
        setIsHorizontalMenu(false);
        return;
      }
      setIsHorizontalMenu(window.innerWidth > 680); // Adjust threshold as needed      
    };
    handleResize(); // Check on initial load
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLoggedIn]);

  // Listen for theme change events so logo can update immediately
  useEffect(() => {
    const onTheme = (e) => setTheme(e?.detail || llStorage.getItem('theme') || 'dark');
    window.addEventListener('ll:themeChange', onTheme);
    return () => window.removeEventListener('ll:themeChange', onTheme);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (event) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavigation = (action) => {
    setMenuOpen(false); // Close the menu after navigation
    if (action === 'logout') {
      llStorage.removeItem('token');       
      llStorage.removeItem('refreshToken');      
      llStorage.removeItem('userImage');      
      llStorage.removeItem('userName');    
      llStorage.removeItem('userInitialsShort4Chars');
      llStorage.removeItem('userLLPNClientExtCutNo');
      llStorage.removeItem('userId');
      llStorage.removeItem('keepLoggedIn');
      llStorage.removeItem('userPlanSubscription');
      llStorage.removeItem('userPlanSubscriptionStatus');
      llStorage.removeItem('showHiddenFeatures');
      llStorage.removeItem('goodnessSponsor');
      setIsLoggedIn(false); // Update the login state
      navigate(`/`); // Redirect to the home page
    } else {
      navigate(`/${action}`);
    }
  };

  const handleShopNavigation = () => {
    window.open('https://lostlink.store', '_blank');
  }

  const menuButtonInitials = (userInitialsShort4Chars || '').trim().slice(0, 4);
  const showMenuButtonInitials = menuButtonInitials.length > 0;
  const showImageOnlyMenuButton = Boolean(userImage) && !showMenuButtonInitials;
  const canUseApiKeys = hasFeatureAccess('pro');
  const menuButtonStyle = userImage
    ? {
        background: `url(${userImage}) center / cover no-repeat`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: '#f0e6d2',
      }
    : undefined;

  return (
    <div className="header-container">
      {/* Logo and Menu Row */}
      <div className="top-row">
        <div className="logo-container">
          <img
            src={theme === 'dark' ? '/images/LLAPP_LOGO_512x176-W.png' : '/images/LLAPP_LOGO_512x176-B.png'}
            alt="Lost Link"
            className="logo"
            onClick={() => handleNavigation(isLoggedIn ? 'dashboard' : '')}
          />
        </div>
        
        <div className={`menu-container ${isHorizontalMenu ? 'horizontal-menu' : ''}`} ref={menuContainerRef}>
          {isHorizontalMenu ? (
            <div className="horizontal-menu-buttons">              
              {(!testMode || isLoggedIn) && location.pathname !== '/About' && <span onClick={() => handleNavigation('About')}>{t('header.about', language)}</span>}
              {location.pathname !== '/' && <span onClick={() => handleNavigation('')}>{t('header.home', language)}</span>}              
              {!isLoggedIn ? (
                location.pathname !== '/login' && <span onClick={() => handleNavigation('login')}>{t('header.login', language)}</span>
              ) : (
                <>
                  {location.pathname !== '/dashboard' && <span onClick={() => handleNavigation('dashboard')}>{t('header.dashboard', language)}</span>}
                  {location.pathname !== '/messages' && (
                    <span onClick={() => handleNavigation('messages')}>
                      {t('header.messages', language)}
                      {newMessageCount > 0 && <span className="new-message-count">({newMessageCount})</span>}
                    </span>
                  )}
                  {location.pathname !== '/files' && <span onClick={() => handleNavigation('files')}>{t('header.files', language) || 'Files'}</span>}
                  {isAdmin && !location.pathname.startsWith('/skus') && <span onClick={() => handleNavigation('skus')}>SKUs</span>}
                  {location.pathname !== '/tag-templates' && <span onClick={() => handleNavigation('tag-templates')}>{t('header.tagTemplates', language) || 'Tag Templates'}</span>}
                  {canUseApiKeys && !location.pathname.startsWith('/api-keys') && <span onClick={() => handleNavigation('api-keys')}>{t('header.apiKeys', language) || 'API Keys'}</span>}
                  {location.pathname !== '/settings' && <span onClick={() => handleNavigation('settings')}>{t('header.settings', language)}</span>}
                  {canManageLlpnCustomerCards && location.pathname !== '/llpn-customer-cards' && <span onClick={() => handleNavigation('llpn-customer-cards')}>{t('header.llpnCustomerCards', language) || 'LLPN Cards'}</span>}
                  <span onClick={() => handleNavigation('logout')}>{t('header.logout', language)}</span>
                </>
              )}
              {(!testMode || isLoggedIn) && location.pathname !== '/shop' && <span onClick={() => handleShopNavigation()}>{t('header.shop', language)}</span>}
              <span className="lang-select">
                  <select
                  value={language}
                  onChange={handleLanguageChange}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    background: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer',
                    }}
                  aria-label="Select language"
                >
                  {sortedLanguageOptions.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {languageFlags[lang.code]} {lang.label}
                    </option>
                  ))}
                </select>
              </span>              
            </div>
          ) : (
            <>
              <button
                className={`menu-button ${showMenuButtonInitials ? 'menu-button-with-initials' : ''} ${userImage ? 'menu-button-has-image' : ''}`}
                style={menuButtonStyle}
                onClick={handleMenuToggle}
                aria-label="Open menu"
              >
                {showMenuButtonInitials ? (
                  <>
                    <span className="menu-button-placeholder" aria-hidden="true">☰</span>
                    <span className="menu-button-initials">{menuButtonInitials}</span>
                  </>
                ) : showImageOnlyMenuButton ? (
                  <span className="menu-button-placeholder" aria-hidden="true">☰</span>
                ) : (
                  '☰'
                )}
              </button>
              {menuOpen && (
                <div className="dropdown-menu">
                  {location.pathname !== '/' && <button onClick={() => handleNavigation('')}>{t('header.home', language)}</button>}
                  {!isLoggedIn ? (
                    location.pathname !== '/login' && <button onClick={() => handleNavigation('login')}>{t('header.login', language)}</button>
                  ) : (
                    <>
                      {location.pathname !== '/dashboard' && <button onClick={() => handleNavigation('dashboard')}>{t('header.dashboard', language)}</button>}
                      {location.pathname !== '/messages' && (
                        <button onClick={() => handleNavigation('messages')}>
                          {t('header.messages', language)}
                          {newMessageCount > 0 && <span className="new-message-count">({newMessageCount})</span>}
                        </button>
                      )}
                      {location.pathname !== '/files' && <button onClick={() => handleNavigation('files')}>{t('header.files', language) || 'Files'}</button>}
                      {isAdmin && !location.pathname.startsWith('/skus') && <button onClick={() => handleNavigation('skus')}>SKUs</button>}
                      {location.pathname !== '/tag-templates' && <button onClick={() => handleNavigation('tag-templates')}>{t('header.tagTemplates', language) || 'Tag Templates'}</button>}
                      {canUseApiKeys && !location.pathname.startsWith('/api-keys') && <button onClick={() => handleNavigation('api-keys')}>{t('header.apiKeys', language) || 'API Keys'}</button>}
                      {location.pathname !== '/settings' && <button onClick={() => handleNavigation('settings')}>{t('header.settings', language)}</button>}
                      {canManageLlpnCustomerCards && location.pathname !== '/llpn-customer-cards' && <button onClick={() => handleNavigation('llpn-customer-cards')}>{t('header.llpnCustomerCards', language) || 'LLPN Cards'}</button>}
                      {canManageLlpnCustomerCards && location.pathname !== '/llpn-aliases' && <button onClick={() => handleNavigation('llpn-aliases')}>{t('header.llpnAliases', language) || 'Aliases'}</button>}
                      {canManageLlpnCustomerCards && location.pathname !== '/llpn-emails' && <button onClick={() => handleNavigation('llpn-emails')}>{t('header.llpnEmails', language) || 'Emails'}</button>}
                      {canManageLlpnCustomerCards && location.pathname !== '/llpn-ocr-jobs' && <button onClick={() => handleNavigation('llpn-ocr-jobs')}>{t('header.llpnOcrJobs', language) || 'OCR Jobs'}</button>}
                      <button onClick={() => handleNavigation('logout')}>{t('header.logout', language)}</button>
                    </>
                  )}
                  {(!testMode || isLoggedIn) && location.pathname !== '/About' && <button onClick={() => handleNavigation('About')}>{t('header.about', language)}</button>}
                  {(!testMode || isLoggedIn) && location.pathname !== '/shop' && <button onClick={() => handleShopNavigation()}>{t('header.shop', language)}</button>}
                  <div style={{ padding: '8px 16px' }}>
                    <select
                      value={language}
                      onChange={handleLanguageChange}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: '#fff',
                        fontSize: '14px',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                      aria-label="Select language"
                    >
                      {sortedLanguageOptions.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {languageFlags[lang.code]} {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Slogan Row - hidden when logged in */}
      {!isLoggedIn && !testMode && (
        <div className="slogan-container">
          <h1 id="main-heading">
            {Array.isArray(slogans) ? slogans[currentSlogan] : slogans}
          </h1>
        </div>
      )}      
    </div>
  );
}

export default Header;